import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField, Modal } from "@fawkes/ui";
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  ArrowRightLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  Info,
  LogIn,
  LogOut,
  PawPrint,
  Pill,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  UserRound,
  Users,
  Wrench,
  X,
} from "lucide-react";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { formatEuro, todayIso } from "../i18n/format";
import type { PensionReserveringDetail } from "./pensionKlantTypes";
import { AccommodatieBeheer } from "./pensionplanning/AccommodatieBeheer";
import {
  AccommodatieIcoon,
  DiersoortIcoon,
  meervoud,
  type AccommodatieType,
  type Prijsberekening,
} from "./pensionplanning/accommodatie";
import "./PensionPlanning.css";

type Status = "GERESERVEERD" | "INGECHECKT" | "UITGECHECKT";
type BlokkadeSoort = "ONDERHOUD" | "GEBLOKKEERD";
type Toon = "groen" | "accent" | "grijs" | "rood" | "paars" | "amber";

interface Kennel {
  id: number;
  naam: string | null;
  nummer: number | null;
  groepId: number | null;
  groep: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  /** Het accommodatietype van de groep; ingesteld of afgeleid uit de groepsnaam, leeg als er geen past. */
  accommodatieId: number | null;
  accommodatieIngesteld: boolean;
  capaciteit: number;
  capaciteitIngesteld: boolean;
}

interface Boeking {
  regelId: number;
  reserveringId: number;
  reserveringNummer: number | null;
  dier: {
    id: number;
    naam: string | null;
    diersoortId: number | null;
    diersoort: string | null;
    ras: string | null;
    geslacht: string | null;
    gewichtKg: number | null;
    eigenschap: string | null;
    eigenschapKleur: string | null;
    medicatie: boolean;
  };
  klant: {
    id: number;
    naam: string | null;
    adres: string | null;
    woonplaats: string | null;
    telefoon: string | null;
    mobiel: string | null;
  };
  van: string;
  vanDagdeel: "VM" | "NM" | null;
  tot: string;
  totDagdeel: "VM" | "NM" | null;
  status: Status;
  ingechecktOp: string | null;
  uitgechecktOp: string | null;
  prijsCategorie: string | null;
  prijsLaag: number | null;
  prijsHoog: number | null;
  eersteBezoek: boolean;
  opmerking: string | null;
  /** `null` = nog niet geplaatst; dat is de normale toestand van een nieuwe boeking. */
  kennelId: number | null;
  /** Elke keer dat het dier in een hok ging, oudste eerst. */
  plaatsingen: { kennelId: number; vanaf: string | null }[];
}

interface Blokkade {
  id: number;
  kennelId: number;
  van: string;
  tot: string;
  soort: BlokkadeSoort;
  omschrijving: string | null;
  door: string | null;
}

interface PlanningData {
  vestigingId: number;
  van: string;
  tot: string;
  prijsberekening: Prijsberekening;
  diersoorten: { id: number; naam: string | null }[];
  accommodaties: AccommodatieType[];
  kennels: Kennel[];
  boekingen: Boeking[];
  blokkades: Blokkade[];
  dagen: { datum: string; aankomsten: number; vertrekken: number; aanwezig: number }[];
}

const WEERGAVEN = [1, 7, 14, 31];
/** Langer dan dit wordt het bord onleesbaar breed. */
const MAX_DAGEN = 62;
type StatusFilter = Status | "GEBLOKKEERD";
const STATUSSEN: StatusFilter[] = ["GERESERVEERD", "INGECHECKT", "UITGECHECKT", "GEBLOKKEERD"];
/** In het filter op accommodatie staat dit voor de hokken zonder type. */
const ZONDER_TYPE = -1;

interface Filters {
  /** Eén diersoort, of `null` voor alle soorten. */
  diersoort: number | null;
  /** De accommodatietypes die niet getoond worden; standaard alles zichtbaar. */
  verborgenTypes: number[];
  status: StatusFilter[];
  eersteBezoek: boolean;
  luxe: boolean;
  nogTePlaatsen: boolean;
}

const FILTERS_OPSLAG = "fawkes.pensionPlanning.filters";
const VELDEN_OPSLAG = "fawkes.pensionPlanning.teplaatsenVelden";

/** Wat er op een kaartje van "Nog te plaatsen" kan staan, in de volgorde waarin het getoond wordt. */
const KAARTVELDEN = ["soort", "geslacht", "eigenaar", "telefoon", "woonplaats", "periode", "nachten", "tarief", "reservering", "opmerking", "chips"] as const;
type Kaartveld = (typeof KAARTVELDEN)[number];
const STANDAARD_KAARTVELDEN: Kaartveld[] = ["soort", "eigenaar", "periode", "nachten", "chips"];

function leesKaartvelden(): Kaartveld[] {
  try {
    const ruw = window.localStorage.getItem(VELDEN_OPSLAG);
    if (!ruw) return STANDAARD_KAARTVELDEN;
    const lijst = JSON.parse(ruw) as unknown;
    return Array.isArray(lijst) ? lijst.filter((x): x is Kaartveld => (KAARTVELDEN as readonly string[]).includes(x as string)) : STANDAARD_KAARTVELDEN;
  } catch {
    return STANDAARD_KAARTVELDEN;
  }
}

const STANDAARD_FILTERS: Filters = {
  diersoort: null,
  verborgenTypes: [],
  status: STATUSSEN,
  eersteBezoek: false,
  luxe: false,
  nogTePlaatsen: false,
};

const DRAG_TYPE = "application/x-fawkes-pensionplanning";
const LUXE = /luxe|suite/i;
const HOND = /hond/i;
const KAT = /kat/i;
/** Een dier met deze eigenschap deelt zijn hok niet. */
const ALLEEN = /alleen|niet sociaal/i;
/** Zoveel voorstellen geeft de groepsmatch. */
const VOORSTELLEN = 5;

type T = (key: string, options?: { args?: unknown[] }) => string;

// ── Datums ──────────────────────────────────────────────────────────────────────────────────────────

/**
 * Een datumveld geeft tijdens het typen van het jaartal tussenwaarden als "0002-08-07" of een jaar van vijf
 * cijfers. Daar valt niet mee te rekenen en er hoort ook geen aanroep bij; pas een echte datum telt.
 */
function geldigeDatum(waarde: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(waarde) && waarde >= "1900-01-01" && !Number.isNaN(Date.parse(`${waarde}T00:00:00Z`));
}

function schuif(iso: string, dagen: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dagen);
  return d.toISOString().slice(0, 10);
}

function verschil(van: string, tot: string): number {
  return Math.round((Date.parse(`${tot}T00:00:00Z`) - Date.parse(`${van}T00:00:00Z`)) / 864e5);
}

function reeks(van: string, tot: string): string[] {
  const dagen: string[] = [];
  for (let d = van; d <= tot; d = schuif(d, 1)) dagen.push(d);
  return dagen;
}

function datum(iso: string, locale: string, opties: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, { ...opties, timeZone: "UTC" }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`));
}

/** Het tandwiel bij "Nog te plaatsen": welke gegevens op een kaartje staan. */
function VeldenKnop({
  t,
  open,
  setOpen,
  gekozen,
  onKies,
}: {
  t: (key: string, options?: { args?: unknown[] }) => string;
  open: boolean;
  setOpen: (v: boolean) => void;
  gekozen: Kaartveld[];
  onKies: (lijst: Kaartveld[]) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const weg = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", weg);
    return () => document.removeEventListener("mousedown", weg);
  }, [open, setOpen]);
  const aan = new Set(gekozen);
  return (
    <span className="kp-velden" ref={ref}>
      <button
        type="button"
        className="kp-inklap"
        aria-expanded={open}
        title={t("pensionPlanning.nogTePlaatsen.velden")}
        aria-label={t("pensionPlanning.nogTePlaatsen.velden")}
        onClick={() => setOpen(!open)}
      >
        <Settings2 size={15} aria-hidden="true" />
      </button>
      {open && (
        <div className="kp-velden__paneel" role="dialog" aria-label={t("pensionPlanning.nogTePlaatsen.velden")}>
          <strong>{t("pensionPlanning.nogTePlaatsen.velden")}</strong>
          <div className="kp-velden__lijst">
            {KAARTVELDEN.map((veld) => (
              <label key={veld}>
                <input
                  type="checkbox"
                  checked={aan.has(veld)}
                  onChange={(e) => onKies(KAARTVELDEN.filter((x) => (x === veld ? e.target.checked : aan.has(x))))}
                />
                {t(`pensionPlanning.kaartveld.${veld}`)}
              </label>
            ))}
          </div>
          <button type="button" className="kp-link" onClick={() => onKies(STANDAARD_KAARTVELDEN)}>
            {t("pensionPlanning.nogTePlaatsen.veldenStandaard")}
          </button>
        </div>
      )}
    </span>
  );
}

interface AdviesKandidaat {
  kennelId: number;
  kennel: string | null;
  groep: string | null;
  accommodatie: string | null;
  plaatsenVrij: number;
  capaciteit: number;
  redenen: { code: string; waarde: string | null }[];
}

interface Advies {
  huidigeKennelId: number | null;
  kandidaten: AdviesKandidaat[];
}

/**
 * Ligt het dier nog nergens, dan staat hier meteen welk verblijf het beste past — dezelfde afweging als het
 * advies op het klantscherm, zodat de balie niet eerst de groepsmatch hoeft te openen.
 */
function AiTip({
  reserveringId,
  dierId,
  t,
  bezig,
  onPlaats,
}: {
  reserveringId: number;
  dierId: number;
  t: T;
  bezig: boolean;
  onPlaats: (kennelId: number) => void;
}) {
  const { data, isLoading, isError } = useCustom<Advies>({
    url: `pension/reserveringen/${reserveringId}/dieren/${dierId}/kenneladvies`,
    method: "get",
    queryOptions: { retry: false },
  });
  const beste = data?.data?.kandidaten?.[0];
  if (isLoading) {
    return (
      <p className="kp-tip" data-leeg="">
        {t("common.loading")}
      </p>
    );
  }
  if (isError || !beste) {
    return (
      <p className="kp-tip" data-leeg="">
        <Sparkles size={15} aria-hidden="true" />
        {t("pensionPlanning.tip.geen")}
      </p>
    );
  }
  const redenen = beste.redenen
    .map((r) => t(`pensionKlant.res.dier.advies.reden.${r.code}`, r.waarde ? { args: [r.waarde] } : undefined))
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="kp-tip">
      <Sparkles size={15} aria-hidden="true" />
      <span className="kp-tip__tekst">
        <strong>{t("pensionPlanning.tip.voorstel", { args: [beste.kennel ?? "—"] })}</strong>
        <span>{redenen}</span>
      </span>
      <button type="button" className="kp-knop kp-knop--hoofd" disabled={bezig} onClick={() => onPlaats(beste.kennelId)}>
        {t("pensionPlanning.tip.plaats")}
      </button>
    </div>
  );
}

/** "20-12-2025 09:04", zoals de balie een tijdstip leest. */
function moment(iso: string): string {
  return `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)} ${iso.slice(11, 16)}`;
}

// ── Het bord ────────────────────────────────────────────────────────────────────────────────────────

/** Eén balk op het bord: de dieren van één reservering in één hok over een aaneengesloten periode, of een blokkade. */
interface Balk {
  sleutel: string;
  kennelId: number;
  van: string;
  tot: string;
  boekingen: Boeking[];
  blokkade?: Blokkade;
}

/**
 * De stukken van een boeking per hok. Een dier zit in een hok tot de volgende plaatsing; wie op de dag
 * van aankomst nog verhuisd werd, zat in het eerste hok dus nooit.
 */
function segmenten(b: Boeking): { kennelId: number; van: string; tot: string }[] {
  const lijst = b.plaatsingen.length > 0 ? b.plaatsingen : b.kennelId != null ? [{ kennelId: b.kennelId, vanaf: b.van }] : [];
  const uit: { kennelId: number; van: string; tot: string }[] = [];
  lijst.forEach((p, i) => {
    const van = p.vanaf && p.vanaf > b.van ? p.vanaf : b.van;
    const volgende = lijst[i + 1]?.vanaf;
    const eind = volgende ? schuif(volgende, -1) : b.tot;
    const tot = eind < b.tot ? eind : b.tot;
    if (tot >= van) uit.push({ kennelId: p.kennelId, van, tot });
  });
  if (uit.length === 0 && b.kennelId != null) uit.push({ kennelId: b.kennelId, van: b.van, tot: b.tot });
  return uit;
}

/** De dieren van één reservering die samen in een hok zitten, worden één balk: "Puk + Luna". */
function boekingBalken(boekingen: Boeking[]): Balk[] {
  const perSleutel = new Map<string, Balk>();
  for (const b of boekingen) {
    for (const s of segmenten(b)) {
      const sleutel = `${b.reserveringId}|${s.kennelId}|${s.van}|${s.tot}`;
      const balk = perSleutel.get(sleutel);
      if (balk) balk.boekingen.push(b);
      else perSleutel.set(sleutel, { sleutel, kennelId: s.kennelId, van: s.van, tot: s.tot, boekingen: [b] });
    }
  }
  return [...perSleutel.values()];
}

/** Legt overlappende balken in een hok onder elkaar; geeft per balk zijn baan en het aantal banen. */
function banen(balken: Balk[]): { balken: { balk: Balk; baan: number }[]; aantal: number } {
  const eindes: string[] = [];
  const uit = [...balken]
    .sort((a, b) => a.van.localeCompare(b.van) || a.sleutel.localeCompare(b.sleutel))
    .map((balk) => {
      let baan = eindes.findIndex((eind) => eind < balk.van);
      if (baan === -1) {
        baan = eindes.length;
        eindes.push(balk.tot);
      } else {
        eindes[baan] = balk.tot;
      }
      return { balk, baan };
    });
  return { balken: uit, aantal: Math.max(1, eindes.length) };
}

/** Hoeveel dieren er op een dag in een hok zitten, eventueel zonder die van één reservering. */
function bezetting(balken: Balk[], kennelId: number, dag: string, behalve?: number): number {
  return balken
    .filter((b) => b.kennelId === kennelId && b.van <= dag && b.tot >= dag)
    .reduce((som, b) => som + b.boekingen.filter((x) => x.reserveringId !== behalve).length, 0);
}

function isLuxe(b: Boeking): boolean {
  return LUXE.test(b.prijsCategorie ?? "");
}

/** Diersoorten blijven uit elkaar: een dier past alleen in een hok van zijn eigen soort. */
function pastSoort(kennel: Kennel, diersoortId: number | null): boolean {
  return kennel.diersoortId == null || diersoortId == null || kennel.diersoortId === diersoortId;
}

function namen(boekingen: Boeking[]): string {
  return boekingen.map((b) => b.dier.naam ?? "—").join(" + ");
}

function kennelNaam(k: Kennel): string {
  return k.naam ?? (k.nummer != null ? String(k.nummer) : "—");
}

interface Voorstel {
  kennel: Kennel;
  past: boolean;
  score: number;
  bezet: number;
  gezin: boolean;
  redenen: ("luxe" | "gezin" | "leeg" | "accommodatie")[];
}

/**
 * De groepsmatch: welke hokken passen bij deze dieren over hun hele verblijf in beeld. Een hok past als het
 * dezelfde diersoort heeft, geen quarantaine is, niet dicht is, en op geen enkele dag vol raakt. Dan telt:
 * luxe geboekt en een luxe type, bij het eigen gezin, en een rustig hok. Een dier dat alleen moet, krijgt
 * alleen lege hokken.
 */
function groepsmatch(
  groep: Boeking[],
  kennels: Kennel[],
  typen: Map<number, AccommodatieType>,
  balken: Balk[],
  blokkades: Blokkade[],
  venster: { van: string; tot: string },
): Voorstel[] {
  const eerste = groep[0];
  const van = eerste.van > venster.van ? eerste.van : venster.van;
  const tot = eerste.tot < venster.tot ? eerste.tot : venster.tot;
  const dagen = van <= tot ? reeks(van, tot) : [];
  const luxe = groep.some(isLuxe);
  const alleen = groep.some((b) => ALLEEN.test(b.dier.eigenschap ?? ""));
  return kennels
    .filter((k) => pastSoort(k, eerste.dier.diersoortId) && !typen.get(k.accommodatieId ?? ZONDER_TYPE)?.quarantaine)
    .map((kennel, volgorde) => {
      const typeLuxe = Boolean(typen.get(kennel.accommodatieId ?? ZONDER_TYPE)?.luxe);
      const bezet = Math.max(0, ...dagen.map((dag) => bezetting(balken, kennel.id, dag, eerste.reserveringId)));
      const gezin = balken.some(
        (b) =>
          b.kennelId === kennel.id &&
          b.van <= tot &&
          b.tot >= van &&
          b.boekingen.some((x) => x.klant.id === eerste.klant.id && x.reserveringId !== eerste.reserveringId),
      );
      const dicht = blokkades.some((b) => b.kennelId === kennel.id && b.van <= tot && b.tot >= van);
      const past = !dicht && bezet + groep.length <= kennel.capaciteit && !(alleen && bezet > 0);
      const redenen: Voorstel["redenen"] = [];
      let score = 10 - bezet - volgorde / 1000;
      if (luxe && typeLuxe) {
        score += 5;
        redenen.push("luxe");
      } else if (luxe || typeLuxe) {
        score -= 4;
        redenen.push("accommodatie");
      }
      if (gezin) {
        score += 6;
        redenen.push("gezin");
      }
      if (bezet === 0) redenen.push("leeg");
      return { kennel, past, score, bezet, gezin, redenen };
    });
}

/** "€ 36,50 per dag" of "+ € 22,00 per dag": de prijs van een type, zoals de vestiging ermee rekent. */
function typePrijs(type: AccommodatieType | undefined, berekening: Prijsberekening, t: T, locale: string): string | null {
  const p = type?.prijs;
  if (!p) return null;
  const bedragen =
    p.bedragHoog != null && p.bedragHoog !== p.bedrag
      ? `${formatEuro(p.bedrag, locale)} / ${formatEuro(p.bedragHoog, locale)}`
      : formatEuro(p.bedrag, locale);
  return berekening === "ACCOMMODATIE"
    ? t("pensionPlanning.paneel.prijsDag", { args: [bedragen] })
    : t("pensionPlanning.paneel.prijsToeslag", { args: [bedragen] });
}

// ── Het scherm ──────────────────────────────────────────────────────────────────────────────────────

type Keuze =
  | { soort: "verblijf"; reserveringId: number; kennelId: number | null; dierIds: number[] }
  | { soort: "hok"; kennelId: number };

type Venster = { soort: "match" | "verplaatsen"; reserveringId: number; dierIds: number[] } | { soort: "hokMatch"; kennelId: number };

/**
 * De kennelplanning in de werkmodus Pension, naar het ontwerp: bovenin de kerncijfers, links de filters,
 * in het midden de hokken per diersoort en groep met de verblijven als balken over de dagen, rechts het
 * gekozen verblijf of hok, en onderin wie er nog geplaatst moet worden.
 *
 * <p>Diersoorten blijven uit elkaar: elke soort heeft zijn eigen deel van het bord, zijn eigen bezetting en
 * zijn eigen accommodatietypes, en een dier gaat alleen naar een hok van zijn soort. De accommodatietypes
 * maakt het pension zelf aan, met een icoon of logo; dat beheer opent hier en in Instellingen › Pension.
 *
 * <p>Dit is een ander scherm dan de asielplanning, niet dezelfde met een filter erop. Een pensionboeking
 * staat in {@code pension_reservering} met zijn hokindeling in {@code kennel_verblijf}; de asielplanning
 * leest {@code asiel_verblijf} + {@code kennel_asiel_periode} en kent die boekingen dus niet.
 */
export function PensionPlanning({ onOpenKlant }: { onOpenKlant?: (klantId: number) => void }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const vandaag = todayIso();
  const [van, setVan] = useState(vandaag);
  const [periodeDagen, setPeriodeDagen] = useState(14);
  const tot = schuif(van, periodeDagen - 1);
  /** Een einddatum kiezen bepaalt de lengte van de periode; korter dan een dag of langer dan het maximum kan niet. */
  const zetTot = (nieuw: string) => {
    if (nieuw < van) return;
    setPeriodeDagen(Math.min(MAX_DAGEN, verschil(van, nieuw) + 1));
  };
  const [filters, setFilters] = useState<Filters>(STANDAARD_FILTERS);
  // Ingeklapt of niet blijft in deze browser bewaard; een balie die de filters niet gebruikt houdt zo ruimte
  // over voor de dieren die nog een hok moeten krijgen.
  const [kaartvelden, setKaartvelden] = useState<Kaartveld[]>(leesKaartvelden);
  const [veldenOpen, setVeldenOpen] = useState(false);
  /** Of er nu een dier boven de lijst hangt; dan licht het vak op als plek om los te laten. */
  const [sleepNaarLijst, setSleepNaarLijst] = useState(false);
  const [bereikOpen, setBereikOpen] = useState(false);
  const bereikRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!bereikOpen) return;
    const weg = (e: MouseEvent) => {
      if (bereikRef.current && !bereikRef.current.contains(e.target as Node)) setBereikOpen(false);
    };
    document.addEventListener("mousedown", weg);
    return () => document.removeEventListener("mousedown", weg);
  }, [bereikOpen]);
  const zetKaartvelden = (lijst: Kaartveld[]) => {
    setKaartvelden(lijst);
    try {
      window.localStorage.setItem(VELDEN_OPSLAG, JSON.stringify(lijst));
    } catch {
      // Geen opslag: dan geldt de keuze tot de pagina dichtgaat.
    }
  };
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      return window.localStorage.getItem(FILTERS_OPSLAG) !== "dicht";
    } catch {
      return true;
    }
  });
  const zetFiltersOpen = (open: boolean) => {
    setFiltersOpen(open);
    try {
      window.localStorage.setItem(FILTERS_OPSLAG, open ? "open" : "dicht");
    } catch {
      // Geen opslag: dan geldt de keuze tot de pagina dichtgaat.
    }
  };
  const [zoek, setZoek] = useState("");
  const [keuze, setKeuze] = useState<Keuze | null>(null);
  const [venster, setVenster] = useState<Venster | null>(null);
  const [beheer, setBeheer] = useState(false);
  const [fout, setFout] = useState(false);

  const { data, isLoading, isError, refetch, dataUpdatedAt } = useCustom<PlanningData>({
    url: "pension/planning",
    method: "get",
    config: { query: { van, tot } },
  });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const planning = data?.data;
  const kennels = useMemo(() => planning?.kennels ?? [], [planning]);
  const boekingen = useMemo(() => planning?.boekingen ?? [], [planning]);
  const blokkades = useMemo(() => planning?.blokkades ?? [], [planning]);
  const diersoorten = useMemo(() => planning?.diersoorten ?? [], [planning]);
  useEffect(() => {
    if (diersoorten.length === 0) return;
    setFilters((f) => (f.diersoort != null && diersoorten.some((d) => d.id === f.diersoort) ? f : { ...f, diersoort: diersoorten[0].id, verborgenTypes: [] }));
  }, [diersoorten]);
  const typen = useMemo(() => new Map((planning?.accommodaties ?? []).map((a) => [a.id, a])), [planning]);
  const berekening = planning?.prijsberekening ?? "PENSIONPRIJZEN";
  const balken = useMemo(() => boekingBalken(boekingen), [boekingen]);
  const dagen = useMemo(() => reeks(van, tot), [van, tot]);
  const fotos = useAnimalPhotoUrls([...new Set(boekingen.map((b) => b.dier.id))]);
  const focus = vandaag >= van && vandaag <= tot ? vandaag : van;
  const typeVan = (k: Kennel | undefined) => (k?.accommodatieId != null ? typen.get(k.accommodatieId) : undefined);

  const voerUit = async (acties: (() => Promise<unknown>)[]) => {
    setFout(false);
    try {
      for (const actie of acties) await actie();
      return true;
    } catch {
      setFout(true);
      return false;
    } finally {
      void refetch();
    }
  };
  const stil = { successNotification: false as const, errorNotification: false as const };
  const plaats = async (reserveringId: number, dierIds: number[], kennelId: number) => {
    const gelukt = await voerUit(
      dierIds.map((dierId) => () =>
        mutateAsync({ url: "pension/planning/plaatsingen", method: "put", values: { reserveringId, dierId, kennelId }, ...stil }),
      ),
    );
    if (gelukt) {
      setVenster(null);
      setKeuze({ soort: "verblijf", reserveringId, kennelId, dierIds });
    }
  };
  const uitHok = async (reserveringId: number, dierIds: number[]) => {
    const gelukt = await voerUit(
      dierIds.map((dierId) => () =>
        mutateAsync({
          url: `pension/planning/plaatsingen?reserveringId=${reserveringId}&dierId=${dierId}`,
          method: "delete",
          values: {},
          ...stil,
        }),
      ),
    );
    if (gelukt) setKeuze({ soort: "verblijf", reserveringId, kennelId: null, dierIds });
  };

  // ── Filteren ──
  const term = zoek.trim().toLowerCase().replace(/^#r?/, "");
  const pastZoek = (bs: Boeking[]) =>
    term === "" ||
    bs.some((b) =>
      [b.dier.naam, b.klant.naam, b.reserveringNummer != null ? String(b.reserveringNummer) : null].some((w) =>
        w?.toLowerCase().includes(term),
      ),
    );
  const pastBoekingen = (bs: Boeking[]) =>
    filters.status.includes(bs[0].status) &&
    (!filters.eersteBezoek || bs.some((b) => b.eersteBezoek)) &&
    (!filters.luxe || bs.some(isLuxe)) &&
    pastZoek(bs);
  const zichtbareKennels = kennels.filter(
    (k) =>
      (filters.diersoort == null || k.diersoortId === filters.diersoort) &&
      !filters.verborgenTypes.includes(k.accommodatieId ?? ZONDER_TYPE),
  );
  // Het bord: per diersoort, daarbinnen per groep, in de volgorde van de hokken.
  const soortDelen: { id: number | null; naam: string | null; groepen: { sleutel: string; naam: string; kennels: Kennel[] }[] }[] = [];
  for (const k of zichtbareKennels) {
    let deel = soortDelen.find((d) => d.id === k.diersoortId);
    if (!deel) {
      deel = { id: k.diersoortId, naam: k.diersoort, groepen: [] };
      soortDelen.push(deel);
    }
    const sleutel = String(k.groepId ?? "geen");
    const groep = deel.groepen.find((g) => g.sleutel === sleutel);
    if (groep) groep.kennels.push(k);
    else deel.groepen.push({ sleutel, naam: k.groep ?? t("pensionPlanning.bord.geenGroep"), kennels: [k] });
  }
  const nogTePlaatsen = boekingen.filter((b) => b.kennelId == null && b.status !== "UITGECHECKT");
  const toon = (veld: Kaartveld) => kaartvelden.includes(veld);
  const zichtbaarTePlaatsen = nogTePlaatsen.filter(
    (b) => pastBoekingen([b]) && (filters.diersoort == null || b.dier.diersoortId === filters.diersoort),
  );
  // In het filter staan de types van de gekozen soort, of van alle soorten; plus "zonder type" als dat voorkomt.
  const filterTypen = (planning?.accommodaties ?? []).filter((a) => filters.diersoort == null || a.diersoortId === filters.diersoort);
  const heeftZonderType = kennels.some(
    (k) => k.accommodatieId == null && (filters.diersoort == null || k.diersoortId === filters.diersoort),
  );

  // ── Kerncijfers ──
  // De kerncijfers gaan over het hele pension, ook als het bord één diersoort toont.
  const bezettingSoorten = diersoorten
    .map((d) => {
      const lijst = kennels.filter((k) => k.diersoortId === d.id);
      return {
        soort: d,
        bezet: lijst.reduce((som, k) => som + bezetting(balken, k.id, focus), 0),
        capaciteit: lijst.reduce((som, k) => som + k.capaciteit, 0),
      };
    })
    .filter((s) => s.capaciteit > 0 || s.bezet > 0);
  const opFocus = focus === vandaag ? null : datum(focus, locale, { day: "numeric", month: "short" });

  const gekozenBoekingen =
    keuze?.soort === "verblijf"
      ? boekingen.filter((b) => b.reserveringId === keuze.reserveringId && keuze.dierIds.includes(b.dier.id))
      : [];
  const gekozenKennel =
    keuze?.soort === "hok"
      ? kennels.find((k) => k.id === keuze.kennelId)
      : keuze?.soort === "verblijf"
        ? kennels.find((k) => k.id === (keuze.kennelId ?? gekozenBoekingen[0]?.kennelId))
        : undefined;

  const opSleep = (e: DragEvent, reserveringId: number, dierIds: number[]) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ reserveringId, dierIds }));
    e.dataTransfer.effectAllowed = "move";
  };
  const opLosInLijst = (e: DragEvent) => {
    const ruw = e.dataTransfer.getData(DRAG_TYPE);
    setSleepNaarLijst(false);
    if (!ruw) return;
    e.preventDefault();
    const { reserveringId, dierIds } = JSON.parse(ruw) as { reserveringId: number; dierIds: number[] };
    void uitHok(reserveringId, dierIds);
  };
  const opLos = (e: DragEvent, kennelId: number) => {
    const ruw = e.dataTransfer.getData(DRAG_TYPE);
    if (!ruw) return;
    e.preventDefault();
    const { reserveringId, dierIds } = JSON.parse(ruw) as { reserveringId: number; dierIds: number[] };
    void plaats(reserveringId, dierIds, kennelId);
  };

  const zetFilter = <K extends keyof Filters>(sleutel: K, waarde: Filters[K]) => setFilters((f) => ({ ...f, [sleutel]: waarde }));
  const wissel = <W,>(lijst: W[], waarde: W) => (lijst.includes(waarde) ? lijst.filter((x) => x !== waarde) : [...lijst, waarde]);

  const bijgewerkt = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  const twee = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="pension-planning">
      <header className="kp-kop">
        <span className="kp-kop__icoon" aria-hidden="true">
          <CalendarDays size={24} />
        </span>
        <div className="kp-kop__titel">
          <h1>{t("pensionPlanning.title")}</h1>
          <p>{t("pensionPlanning.subtitle")}</p>
        </div>
        <label className="kp-zoek">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            autoComplete="off"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder={t("pensionPlanning.zoek")}
            aria-label={t("pensionPlanning.zoek")}
          />
        </label>
      </header>

      <section className="kp-kpis" aria-label={t("pensionPlanning.kpi.aria")}>
        <Kpi icoon={<LogIn size={24} />} toon="groen" waarde={boekingen.filter((b) => b.van === focus).length}
          label={opFocus ? t("pensionPlanning.kpi.aankomstenOp", { args: [opFocus] }) : t("pensionPlanning.kpi.aankomsten")} />
        <Kpi icoon={<LogOut size={24} />} toon="accent" waarde={boekingen.filter((b) => b.tot === focus).length}
          label={opFocus ? t("pensionPlanning.kpi.vertrekkenOp", { args: [opFocus] }) : t("pensionPlanning.kpi.vertrekken")} />
        {bezettingSoorten.map((s) => (
          <Kpi
            key={s.soort.id}
            icoon={<DiersoortIcoon naam={s.soort.naam} size={24} />}
            toon={KAT.test(s.soort.naam ?? "") ? "paars" : "accent"}
            waarde={`${s.bezet} / ${s.capaciteit}`}
            label={t("pensionPlanning.kpi.bezetting", { args: [meervoud(s.soort.naam)] })}
            meter={s.capaciteit > 0 ? s.bezet / s.capaciteit : null}
            meterToon={KAT.test(s.soort.naam ?? "") ? "paars" : "groen"}
          />
        ))}
        <Kpi icoon={<AlertTriangle size={24} />} toon="amber" waarde={nogTePlaatsen.length} label={t("pensionPlanning.kpi.nogTePlaatsen")} />
      </section>

      <div className="kp-lijf" data-paneel={keuze ? "" : undefined}>
        <div className="kp-zijkolom">
        <aside className="kp-filters kp-kaart" aria-label={t("pensionPlanning.filters.titel")}>
          <div className="kp-filters__kop">
            <Filter size={18} aria-hidden="true" />
            <h2>{t("pensionPlanning.filters.titel")}</h2>
            {filtersOpen && (
              <button type="button" className="kp-link" onClick={() => setFilters((f) => ({ ...STANDAARD_FILTERS, diersoort: f.diersoort }))}>
                {t("pensionPlanning.filters.wissen")}
              </button>
            )}
            <button
              type="button"
              className="kp-inklap"
              aria-expanded={filtersOpen}
              aria-controls="kp-filters-lijf"
              title={t(filtersOpen ? "pensionPlanning.filters.inklappen" : "pensionPlanning.filters.uitklappen")}
              aria-label={t(filtersOpen ? "pensionPlanning.filters.inklappen" : "pensionPlanning.filters.uitklappen")}
              onClick={() => zetFiltersOpen(!filtersOpen)}
            >
              {filtersOpen ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
            </button>
          </div>
          <div className="kp-filters__lijf" id="kp-filters-lijf" hidden={!filtersOpen}>

          <FilterGroep titel={t("pensionPlanning.filters.periode")}>
            <div className="kp-periode">
              <DatumVeld waarde={van} label={t("pensionPlanning.filters.van")} onKies={setVan} />
              <span>{t("pensionPlanning.filters.tm")}</span>
              <DatumVeld waarde={tot} min={van} label={t("pensionPlanning.filters.tot")}
                onKies={zetTot} />
            </div>
          </FilterGroep>

          {diersoorten.length > 0 && (
            <FilterGroep titel={t("pensionPlanning.filters.diersoort")}>
              <select
                className="kp-keuze"
                aria-label={t("pensionPlanning.filters.diersoort")}
                value={filters.diersoort ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, diersoort: Number(e.target.value), verborgenTypes: [] }))}
              >
                {diersoorten.map((d) => (
                  <option key={d.id} value={d.id}>
                    {meervoud(d.naam)}
                  </option>
                ))}
              </select>
            </FilterGroep>
          )}

          <FilterGroep titel={t("pensionPlanning.filters.accommodatie")}>
            {filterTypen.map((a) => (
              <label key={a.id} className="kp-vink">
                <input type="checkbox" checked={!filters.verborgenTypes.includes(a.id)}
                  onChange={() => zetFilter("verborgenTypes", wissel(filters.verborgenTypes, a.id))} />
                <AccommodatieIcoon type={a} size={15} />
                <span>
                  {a.naam}
                </span>
              </label>
            ))}
            {heeftZonderType && (
              <label className="kp-vink">
                <input type="checkbox" checked={!filters.verborgenTypes.includes(ZONDER_TYPE)}
                  onChange={() => zetFilter("verborgenTypes", wissel(filters.verborgenTypes, ZONDER_TYPE))} />
                <PawPrint size={15} aria-hidden="true" />
                {t("pensionPlanning.filters.zonderType")}
              </label>
            )}
            <button type="button" className="kp-link kp-beheerlink" onClick={() => setBeheer(true)}>
              <Settings2 size={14} aria-hidden="true" />
              {t("pensionPlanning.filters.beheren")}
            </button>
          </FilterGroep>

          <FilterGroep titel={t("pensionPlanning.filters.status")}>
            {STATUSSEN.map((s) => (
              <label key={s} className="kp-vink">
                <input type="checkbox" checked={filters.status.includes(s)} onChange={() => zetFilter("status", wissel(filters.status, s))} />
                <span className="kp-stip" data-toon={statusToon(s)} aria-hidden="true" />
                {t(`pensionPlanning.status.${s}`)}
              </label>
            ))}
          </FilterGroep>

          <FilterGroep titel={t("pensionPlanning.filters.extra")}>
            {(["eersteBezoek", "luxe", "nogTePlaatsen"] as const).map((sleutel) => (
              <label key={sleutel} className="kp-schakel">
                <input type="checkbox" role="switch" checked={filters[sleutel]} onChange={(e) => zetFilter(sleutel, e.target.checked)} />
                <span className="kp-schakel__baan" aria-hidden="true" />
                {t(`pensionPlanning.extra.${sleutel}`)}
              </label>
            ))}
          </FilterGroep>
          </div>
        </aside>
        <section
          className="kp-kaart kp-teplaatsen"
          aria-label={t("pensionPlanning.nogTePlaatsen.titel")}
          data-sleep={sleepNaarLijst ? "" : undefined}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setSleepNaarLijst(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setSleepNaarLijst(false);
          }}
          onDrop={opLosInLijst}
        >
          <h2>
            <Users size={18} aria-hidden="true" />
            {t("pensionPlanning.nogTePlaatsen.titel")} <span className="kp-teller">({zichtbaarTePlaatsen.length})</span>
            <VeldenKnop t={t} open={veldenOpen} setOpen={setVeldenOpen} gekozen={kaartvelden} onKies={zetKaartvelden} />
          </h2>
          {sleepNaarLijst ? (
            <p className="kp-sleepvak">{t("pensionPlanning.nogTePlaatsen.sleepHier")}</p>
          ) : zichtbaarTePlaatsen.length === 0 ? (
            <p className="kp-leeg">{t("pensionPlanning.nogTePlaatsen.leeg")}</p>
          ) : (
            <ul className="kp-kaartjes">
              {zichtbaarTePlaatsen.map((b) => (
                <li key={b.regelId}>
                  <button
                    type="button"
                    className="kp-kaartje"
                    draggable
                    onDragStart={(e) => opSleep(e, b.reserveringId, [b.dier.id])}
                    aria-label={`${b.dier.naam ?? "—"}, ${b.klant.naam ?? ""}`}
                    data-gekozen={keuze?.soort === "verblijf" && keuze.kennelId == null && keuze.dierIds.includes(b.dier.id) ? "" : undefined}
                    onClick={() => setKeuze({ soort: "verblijf", reserveringId: b.reserveringId, kennelId: null, dierIds: [b.dier.id] })}
                  >
                    <Foto url={fotos[String(b.dier.id)]} groot />
                    <span className="kp-kaartje__tekst">
                      <strong>{b.dier.naam ?? "—"}</strong>
                      {toon("soort") && <span>{[b.dier.diersoort, b.dier.ras].filter(Boolean).join(" • ")}</span>}
                      {toon("geslacht") && b.dier.geslacht && <span>{b.dier.geslacht}</span>}
                      {toon("eigenaar") && <span>{b.klant.naam}</span>}
                      {toon("telefoon") && (b.klant.mobiel || b.klant.telefoon) && <span>{b.klant.mobiel || b.klant.telefoon}</span>}
                      {toon("woonplaats") && b.klant.woonplaats && <span>{b.klant.woonplaats}</span>}
                      {toon("periode") && (
                        <span>
                          {datum(b.van, locale, { day: "numeric", month: "short" })} – {datum(b.tot, locale, { day: "numeric", month: "short" })}
                        </span>
                      )}
                      {toon("nachten") && <span>{t("pensionPlanning.nachten", { args: [verschil(b.van, b.tot)] })}</span>}
                      {toon("reservering") && b.reserveringNummer != null && <span>R-{b.reserveringNummer}</span>}
                      {toon("opmerking") && b.opmerking && <span className="kp-kaartje__opmerking">{b.opmerking}</span>}
                      {toon("chips") && (
                        <span className="kp-chips">
                          {toon("tarief") && b.prijsCategorie && (
                            <span className="kp-chip" data-toon={isLuxe(b) ? "paars" : "grijs"}>
                              {b.prijsCategorie}
                            </span>
                          )}
                          {b.eersteBezoek && <span className="kp-chip" data-toon="groen">{t("pensionPlanning.chip.eersteBezoek")}</span>}
                          {b.dier.medicatie && <span className="kp-chip" data-toon="rood">{t("pensionPlanning.chip.medicatie")}</span>}
                        </span>
                      )}
                      {!toon("chips") && toon("tarief") && b.prijsCategorie && <span>{b.prijsCategorie}</span>}
                    </span>
                    {b.van <= vandaag && (
                      <span className="kp-urgent" title={t("pensionPlanning.nogTePlaatsen.urgent")}>
                        !<span className="kp-verborgen">{t("pensionPlanning.nogTePlaatsen.urgent")}</span>
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        </div>

        <main className="kp-midden">
          <section className="kp-kaart kp-bordkaart">
            <div className="kp-balk-bediening">
              <button type="button" className="kp-knop" onClick={() => setVan(vandaag)}>
                <CalendarDays size={15} aria-hidden="true" />
                {t("pensionPlanning.vandaag")}
              </button>
              <div className="kp-knoppenpaar">
                <button type="button" className="kp-knop kp-knop--icoon" aria-label={t("pensionPlanning.vorige")}
                  onClick={() => setVan(schuif(van, -periodeDagen))}>
                  <ChevronLeft size={16} aria-hidden="true" />
                </button>
                <button type="button" className="kp-knop kp-knop--icoon" aria-label={t("pensionPlanning.volgende")}
                  onClick={() => setVan(schuif(van, periodeDagen))}>
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
              <span className="kp-bereikvak" ref={bereikRef}>
                <button
                  type="button"
                  className="kp-bereik"
                  aria-expanded={bereikOpen}
                  aria-label={t("pensionPlanning.periode.wijzigen")}
                  title={t("pensionPlanning.periode.wijzigen")}
                  onClick={() => setBereikOpen(!bereikOpen)}
                >
                  {datum(van, locale, { weekday: "short", day: "numeric", month: "short", year: van.slice(0, 4) === tot.slice(0, 4) ? undefined : "numeric" })}
                  {" – "}
                  {datum(tot, locale, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  <CalendarDays size={15} aria-hidden="true" />
                </button>
                {bereikOpen && (
                  <div className="kp-bereik__paneel" role="dialog" aria-label={t("pensionPlanning.periode.wijzigen")}>
                    <div className="kp-periode">
                      <DatumVeld waarde={van} label={t("pensionPlanning.filters.van")} onKies={setVan} />
                      <span>{t("pensionPlanning.filters.tm")}</span>
                      <DatumVeld
                        waarde={tot}
                        min={van}
                        label={t("pensionPlanning.filters.tot")}
                        onKies={zetTot}
                      />
                    </div>
                    <div className="kp-bereik__snel" role="group" aria-label={t("pensionPlanning.weergave.aria")}>
                      {WEERGAVEN.map((w) => (
                        <button key={w} type="button" className="kp-knop" aria-pressed={periodeDagen === w} onClick={() => setPeriodeDagen(w)}>
                          {t(`pensionPlanning.weergave.${w}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </span>
              <div className="kp-weergave" role="group" aria-label={t("pensionPlanning.weergave.aria")}>
                {WEERGAVEN.map((w) => (
                  <button key={w} type="button" className="kp-knop" aria-pressed={periodeDagen === w} onClick={() => setPeriodeDagen(w)}>
                    {t(`pensionPlanning.weergave.${w}`)}
                  </button>
                ))}
              </div>
            </div>

            {isError ? (
              <p className="kp-melding" role="alert">
                {t("pensionPlanning.loadFailed")}{" "}
                <button type="button" className="kp-link" onClick={() => void refetch()}>{t("pensionPlanning.opnieuw")}</button>
              </p>
            ) : isLoading ? (
              <p className="kp-melding">{t("common.loading")}</p>
            ) : kennels.length === 0 ? (
              <p className="kp-melding">{t("pensionPlanning.bord.leeg")}</p>
            ) : (
              <div className="kp-bord-wrap">
                <div className="kp-bord" role="region" aria-label={t("pensionPlanning.bord.aria")}
                  style={{ "--kp-dagen": dagen.length } as CSSProperties}>
                  <div className="kp-rij kp-rij--kop">
                    <div className="kp-label kp-label--kop">{t("pensionPlanning.bord.kop")}</div>
                    <div className="kp-dagen">
                      {dagen.map((dag) => (
                        <div key={dag} className="kp-dag" data-vandaag={dag === vandaag ? "" : undefined}>
                          <span>{datum(dag, locale, { weekday: "short" })}</span>
                          <span>{datum(dag, locale, { day: "numeric", month: "short" })}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {soortDelen.map((deel) => (
                    <section key={deel.id ?? "geen"} className="kp-soort" aria-label={meervoud(deel.naam)}>
                      {soortDelen.length > 1 && (
                        <div className="kp-soort__kop">
                          <DiersoortIcoon naam={deel.naam} size={17} />
                          <h3>{meervoud(deel.naam)}</h3>
                        </div>
                      )}
                      {deel.groepen.map((groep) => {
                        const type = typeVan(groep.kennels[0]);
                        return (
                          <section key={groep.sleutel} className="kp-groep" aria-label={groep.naam}>
                            <div className="kp-groep__kop" data-quarantaine={type?.quarantaine ? "" : undefined}>
                              <AccommodatieIcoon type={type} size={16} />
                              <h3>{groep.naam}</h3>
                              {type && type.naam.toLowerCase() !== groep.naam.toLowerCase() && (
                                <span className="kp-chip" style={{ "--toon": type.kleur } as CSSProperties}>{type.naam}</span>
                              )}
                            </div>
                            {groep.kennels.map((kennel) => {
                              const eigen = filters.nogTePlaatsen
                                ? []
                                : balken.filter(
                                    (b) => b.kennelId === kennel.id && b.van <= tot && b.tot >= van && pastBoekingen(b.boekingen),
                                  );
                              const dicht =
                                filters.status.includes("GEBLOKKEERD") && term === ""
                                  ? blokkades
                                      .filter((b) => b.kennelId === kennel.id)
                                      .map((b): Balk => ({ sleutel: `blokkade-${b.id}`, kennelId: kennel.id, van: b.van, tot: b.tot, boekingen: [], blokkade: b }))
                                  : [];
                              const gelegd = banen([...dicht, ...eigen]);
                              const bezet = bezetting(balken, kennel.id, focus);
                              const naam = kennelNaam(kennel);
                              return (
                                <div key={kennel.id} className="kp-rij" data-gekozen={gekozenKennel?.id === kennel.id ? "" : undefined}>
                                  <button type="button" className="kp-label" aria-label={`${naam}, ${bezet}/${kennel.capaciteit}`}
                                    onClick={() => setKeuze({ soort: "hok", kennelId: kennel.id })}>
                                    <span className="kp-label__naam">{naam}</span>
                                    <span aria-hidden="true">•</span>
                                    <span className="kp-label__bezet" data-vol={bezet >= kennel.capaciteit ? "" : undefined}>
                                      {bezet}/{kennel.capaciteit}
                                    </span>
                                  </button>
                                  <div
                                    className="kp-spoor"
                                    style={{ "--kp-banen": gelegd.aantal } as CSSProperties}
                                    onDragOver={(e) => {
                                      if (e.dataTransfer.types.includes(DRAG_TYPE)) e.preventDefault();
                                    }}
                                    onDrop={(e) => opLos(e, kennel.id)}
                                  >
                                    {vandaag >= van && vandaag <= tot && (
                                      <span className="kp-nu" style={{ "--kp-positie": verschil(van, vandaag) } as CSSProperties} aria-hidden="true" />
                                    )}
                                    {gelegd.balken.map(({ balk, baan }) => (
                                      <BalkKnop
                                        key={balk.sleutel}
                                        balk={balk}
                                        baan={baan}
                                        venster={{ van, tot }}
                                        quarantaine={Boolean(type?.quarantaine)}
                                        fotos={fotos}
                                        t={t}
                                        gekozen={
                                          keuze?.soort === "verblijf" &&
                                          balk.boekingen.some((b) => b.reserveringId === keuze.reserveringId) &&
                                          balk.kennelId === (keuze.kennelId ?? balk.kennelId)
                                        }
                                        onKies={() =>
                                          balk.blokkade
                                            ? setKeuze({ soort: "hok", kennelId: kennel.id })
                                            : setKeuze({
                                                soort: "verblijf",
                                                reserveringId: balk.boekingen[0].reserveringId,
                                                kennelId: kennel.id,
                                                dierIds: balk.boekingen.map((b) => b.dier.id),
                                              })
                                        }
                                        onSleep={(e) => opSleep(e, balk.boekingen[0].reserveringId, balk.boekingen.map((b) => b.dier.id))}
                                      />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </section>
                        );
                      })}
                    </section>
                  ))}
                </div>
              </div>
            )}
          </section>

        </main>

        {keuze?.soort === "verblijf" && gekozenBoekingen.length > 0 && (
          <VerblijfPaneel
            key={`${keuze.reserveringId}|${keuze.dierIds.join(",")}`}
            groep={gekozenBoekingen}
            kennel={keuze.kennelId == null && gekozenBoekingen[0].kennelId == null ? undefined : gekozenKennel}
            type={typeVan(gekozenKennel)}
            kennels={kennels}
            balken={balken}
            focus={focus}
            vandaag={vandaag}
            fotos={fotos}
            t={t}
            locale={locale}
            bezig={bezig}
            fout={fout}
            onSluit={() => setKeuze(null)}
            onOpenKlant={onOpenKlant}
            onPlaats={(kennelId) => void plaats(keuze.reserveringId, keuze.dierIds, kennelId)}
            onUitHok={() => void uitHok(keuze.reserveringId, keuze.dierIds)}
            onVenster={(soort) => setVenster({ soort, reserveringId: keuze.reserveringId, dierIds: keuze.dierIds })}
          />
        )}
        {keuze?.soort === "hok" && gekozenKennel && (
          <HokPaneel
            key={gekozenKennel.id}
            kennel={gekozenKennel}
            typen={(planning?.accommodaties ?? []).filter((a) => gekozenKennel.diersoortId == null || a.diersoortId === gekozenKennel.diersoortId)}
            berekening={berekening}
            balken={balken.filter((b) => b.kennelId === gekozenKennel.id && b.van <= tot && b.tot >= van)}
            blokkades={blokkades.filter((b) => b.kennelId === gekozenKennel.id)}
            bezet={bezetting(balken, gekozenKennel.id, focus)}
            focus={focus}
            t={t}
            locale={locale}
            bezig={bezig}
            fout={fout}
            onSluit={() => setKeuze(null)}
            onKies={(balk) =>
              setKeuze({ soort: "verblijf", reserveringId: balk.boekingen[0].reserveringId, kennelId: balk.kennelId, dierIds: balk.boekingen.map((b) => b.dier.id) })
            }
            onZet={(instelling) =>
              void voerUit([() => mutateAsync({ url: `pension/planning/kennels/${gekozenKennel.id}`, method: "put", values: instelling, ...stil })])
            }
            onBlokkeer={(verzoek) =>
              void voerUit([() => mutateAsync({ url: "pension/planning/blokkades", method: "post", values: { kennelId: gekozenKennel.id, ...verzoek }, ...stil })])
            }
            onOpheffen={(id) => void voerUit([() => mutateAsync({ url: `pension/planning/blokkades/${id}`, method: "delete", values: {}, ...stil })])}
            onGroepsmatch={() => setVenster({ soort: "hokMatch", kennelId: gekozenKennel.id })}
            onBeheer={() => setBeheer(true)}
          />
        )}
      </div>

      <footer className="kp-voet">
        <ul className="kp-legenda">
          <li><span className="kp-stip" data-toon="groen" />{t("pensionPlanning.legenda.ingecheckt")}</li>
          <li><span className="kp-stip" data-toon="accent" />{t("pensionPlanning.legenda.gereserveerd")}</li>
          <li><span className="kp-stip" data-toon="grijs" />{t("pensionPlanning.legenda.uitgecheckt")}</li>
          <li><span className="kp-stip" data-toon="rood" />{t("pensionPlanning.legenda.geblokkeerd")}</li>
          <li><AlertTriangle size={14} aria-hidden="true" data-toon="rood" />{t("pensionPlanning.legenda.quarantaine")}</li>
          <li><Users size={14} aria-hidden="true" />{t("pensionPlanning.legenda.gezin")}</li>
          <li><Pill size={14} aria-hidden="true" data-toon="rood" />{t("pensionPlanning.legenda.medicatie")}</li>
          <li><Sparkles size={14} aria-hidden="true" data-toon="accent" />{t("pensionPlanning.legenda.eersteBezoek")}</li>
        </ul>
        {bijgewerkt && (
          <span className="kp-bijgewerkt">
            {t("pensionPlanning.bijgewerkt", {
              args: [
                `${twee(bijgewerkt.getDate())}-${twee(bijgewerkt.getMonth() + 1)}-${bijgewerkt.getFullYear()} ${twee(bijgewerkt.getHours())}:${twee(bijgewerkt.getMinutes())}`,
              ],
            })}
            <button type="button" className="kp-knop kp-knop--icoon kp-knop--plat" aria-label={t("pensionPlanning.verversen")} onClick={() => void refetch()}>
              <RefreshCw size={14} aria-hidden="true" />
            </button>
          </span>
        )}
      </footer>

      {venster && planning && (
        <MatchVenster
          venster={venster}
          boekingen={boekingen}
          nogTePlaatsen={nogTePlaatsen}
          kennels={kennels}
          typen={typen}
          balken={balken}
          blokkades={blokkades}
          periode={{ van, tot }}
          focus={focus}
          t={t}
          bezig={bezig}
          onSluit={() => setVenster(null)}
          onPlaats={(reserveringId, dierIds, kennelId) => void plaats(reserveringId, dierIds, kennelId)}
        />
      )}

      {beheer && (
        <Modal open size="wide" title={t("pensionPlanning.beheer.titel")} onClose={() => setBeheer(false)}>
          <AccommodatieBeheer vestigingId={planning?.vestigingId} onGewijzigd={() => void refetch()} />
        </Modal>
      )}
    </div>
  );
}

function statusToon(s: StatusFilter): Toon {
  return s === "INGECHECKT" ? "groen" : s === "GERESERVEERD" ? "accent" : s === "UITGECHECKT" ? "grijs" : "rood";
}

// ── Bouwstenen ──────────────────────────────────────────────────────────────────────────────────────

function Kpi({
  icoon,
  toon,
  waarde,
  label,
  meter,
  meterToon,
}: {
  icoon: ReactNode;
  toon: Toon;
  waarde: ReactNode;
  label: string;
  meter?: number | null;
  meterToon?: Toon;
}) {
  return (
    <div className="kp-kaart kp-kpi">
      <span className="kp-kpi__icoon" data-toon={toon} aria-hidden="true">
        {icoon}
      </span>
      <div className="kp-kpi__tekst">
        <strong>{waarde}</strong>
        <span>{label}</span>
        {meter != null && (
          <span className="kp-meter">
            <span className="kp-meter__baan" data-toon={meterToon}>
              <span style={{ width: `${Math.min(100, Math.round(meter * 100))}%` }} />
            </span>
            <span>{Math.round(meter * 100)}%</span>
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Een datumveld dat zijn eigen tekst bijhoudt. Gekoppeld aan de periode zou React elke tussenwaarde van het
 * jaartal terugzetten naar de oude datum, en dan komt de nieuwe er nooit; alleen een echte datum gaat door.
 */
function DatumVeld({ waarde, min, label, onKies }: { waarde: string; min?: string; label: string; onKies: (datum: string) => void }) {
  const [tekst, setTekst] = useState(waarde);
  useEffect(() => setTekst(waarde), [waarde]);
  return (
    <DateField value={tekst}
      min={min}
      aria-label={label}
      onChange={(e) => {
        setTekst(e.target.value);
        if (geldigeDatum(e.target.value)) onKies(e.target.value);
      }}
      onBlur={() => setTekst(waarde)}
    />
  );
}

function FilterGroep({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <fieldset className="kp-filtergroep">
      <legend>{titel}</legend>
      {children}
    </fieldset>
  );
}

function Foto({ url, groot }: { url: string | null | undefined; groot?: boolean }) {
  return (
    <span className="kp-foto" data-groot={groot ? "" : undefined}>
      {url ? <img src={url} alt="" /> : <PawPrint size={groot ? 22 : 12} aria-hidden="true" />}
    </span>
  );
}

function BalkKnop({
  balk,
  baan,
  venster,
  quarantaine,
  fotos,
  t,
  gekozen,
  onKies,
  onSleep,
}: {
  balk: Balk;
  baan: number;
  venster: { van: string; tot: string };
  quarantaine: boolean;
  fotos: Record<string, string>;
  t: T;
  gekozen: boolean;
  onKies: () => void;
  onSleep: (e: DragEvent) => void;
}) {
  const van = balk.van > venster.van ? balk.van : venster.van;
  const tot = balk.tot < venster.tot ? balk.tot : venster.tot;
  const stijl = {
    "--kp-start": verschil(venster.van, van),
    "--kp-lengte": verschil(van, tot) + 1,
    "--kp-baan": baan,
  } as CSSProperties;
  const randen = {
    "data-voor": balk.van < venster.van ? "" : undefined,
    "data-na": balk.tot > venster.tot ? "" : undefined,
  };

  if (balk.blokkade) {
    const tekst = balk.blokkade.omschrijving ?? t(`pensionPlanning.blokkade.${balk.blokkade.soort}`);
    return (
      <button type="button" className="kp-balk" data-toon="rood" style={stijl} {...randen} onClick={onKies}
        aria-label={`${t(`pensionPlanning.blokkade.${balk.blokkade.soort}`)}: ${tekst}`}>
        <Wrench size={14} aria-hidden="true" />
        <span className="kp-balk__tekst">{tekst}</span>
      </button>
    );
  }

  const eerste = balk.boekingen[0];
  const toon: Toon = quarantaine ? "rood" : eerste.status === "INGECHECKT" ? "groen" : eerste.status === "UITGECHECKT" ? "grijs" : "accent";
  const naam = namen(balk.boekingen);
  return (
    <button
      type="button"
      className="kp-balk"
      data-toon={toon}
      data-gekozen={gekozen ? "" : undefined}
      style={stijl}
      {...randen}
      draggable={eerste.status !== "UITGECHECKT"}
      onDragStart={onSleep}
      onClick={onKies}
      aria-label={`${naam}, ${eerste.klant.naam ?? ""}`}
      title={`${naam} • ${eerste.klant.naam ?? ""}`}
    >
      <span className="kp-balk__fotos" aria-hidden="true">
        {balk.boekingen.slice(0, 2).map((b) => (
          <Foto key={b.dier.id} url={fotos[String(b.dier.id)]} />
        ))}
      </span>
      <span className="kp-balk__tekst">
        <strong>{naam}</strong> • {eerste.klant.naam}
      </span>
      {balk.boekingen.length > 1 && <Users size={13} aria-label={t("pensionPlanning.legenda.gezin")} className="kp-balk__teken" />}
      {balk.boekingen.some((b) => b.dier.medicatie) && (
        <Pill size={13} aria-label={t("pensionPlanning.legenda.medicatie")} className="kp-balk__teken" data-toon="rood" />
      )}
      {balk.boekingen.some(isLuxe) && <span className="kp-chip" data-toon="accent">{t("pensionPlanning.chip.luxe")}</span>}
      {balk.boekingen.some((b) => b.eersteBezoek) && (
        <span className="kp-chip" data-toon="groen">{t("pensionPlanning.chip.eersteBezoek")}</span>
      )}
      <ChevronRight size={14} aria-hidden="true" className="kp-balk__pijl" />
    </button>
  );
}

function geslacht(dier: Boeking["dier"]): string | null {
  const man = dier.geslacht === "Mannelijk";
  const vrouw = dier.geslacht === "Vrouwelijk";
  if (!man && !vrouw) return null;
  if (HOND.test(dier.diersoort ?? "")) return man ? "Reu" : "Teef";
  if (KAT.test(dier.diersoort ?? "")) return man ? "Kater" : "Poes";
  return dier.geslacht;
}

function PaneelKop({
  icoon,
  titel,
  sub,
  pil,
  toon,
  t,
  onSluit,
}: {
  icoon: ReactNode;
  titel: string;
  sub: string | null;
  pil: string;
  toon: Toon;
  t: T;
  onSluit: () => void;
}) {
  return (
    <div className="kp-paneel__kop">
      <span className="kp-paneel__icoon" aria-hidden="true">
        {icoon}
      </span>
      <div className="kp-paneel__titel">
        <h2>{titel}</h2>
        {sub && <p>{sub}</p>}
      </div>
      <span className="kp-statuspil" data-toon={toon}>
        <span className="kp-stip" data-toon={toon} aria-hidden="true" />
        {pil}
      </span>
      <button type="button" className="kp-knop kp-knop--icoon kp-knop--plat" aria-label={t("pensionPlanning.paneel.sluiten")} onClick={onSluit}>
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

function VerblijfPaneel({
  groep,
  kennel,
  type,
  kennels,
  balken,
  focus,
  vandaag,
  fotos,
  t,
  locale,
  bezig,
  fout,
  onSluit,
  onOpenKlant,
  onPlaats,
  onUitHok,
  onVenster,
}: {
  groep: Boeking[];
  kennel: Kennel | undefined;
  type: AccommodatieType | undefined;
  kennels: Kennel[];
  balken: Balk[];
  focus: string;
  vandaag: string;
  fotos: Record<string, string>;
  t: T;
  locale: string;
  bezig: boolean;
  fout: boolean;
  onSluit: () => void;
  onOpenKlant?: (klantId: number) => void;
  onPlaats: (kennelId: number) => void;
  onUitHok: () => void;
  onVenster: (soort: "match" | "verplaatsen") => void;
}) {
  const eerste = groep[0];
  const { data } = useCustom<PensionReserveringDetail>({ url: `pension/reserveringen/${eerste.reserveringId}`, method: "get" });
  const detail = data?.data;
  const tags = detail?.tags ?? [];
  const arrangementen = [
    ...new Set([...(detail?.dieren ?? []).flatMap((d) => d.producten.map((p) => p.naam)), ...(detail?.producten ?? []).map((p) => p.naam)]),
  ].filter((n): n is string => Boolean(n));
  const titel = kennel ? kennelNaam(kennel) : namen(groep);
  const [pil, toon]: [string, Toon] = !kennel
    ? [t("pensionPlanning.paneel.nietGeplaatst"), "amber"]
    : eerste.status === "INGECHECKT"
      ? [t("pensionPlanning.paneel.inGebruik"), "groen"]
      : eerste.status === "UITGECHECKT"
        ? [t("pensionPlanning.paneel.uitgecheckt"), "grijs"]
        : [t("pensionPlanning.paneel.gereserveerd"), "accent"];
  const passend = kennels.filter((k) => pastSoort(k, eerste.dier.diersoortId));
  const eigenschappen = [...new Set(groep.map((b) => b.dier.eigenschap).filter((e): e is string => Boolean(e)))];
  const klant = eerste.klant;
  const kanWeg = kennel != null && eerste.status === "GERESERVEERD";

  return (
    <aside className="kp-kaart kp-paneel" aria-label={titel}>
      <PaneelKop
        icoon={kennel ? <AccommodatieIcoon type={type} size={22} /> : <DiersoortIcoon naam={eerste.dier.diersoort} size={22} />}
        titel={titel}
        sub={kennel ? [kennel.groep, type?.naam].filter(Boolean).join(" – ") : [eerste.dier.diersoort, eerste.dier.ras].filter(Boolean).join(" • ")}
        pil={pil}
        toon={toon}
        t={t}
        onSluit={onSluit}
      />

      <div className="kp-paneel__fotos">
        {groep.slice(0, 2).map((b) => (
          <span key={b.dier.id} className="kp-grotefoto">
            {fotos[String(b.dier.id)] ? <img src={fotos[String(b.dier.id)]} alt={b.dier.naam ?? ""} /> : <PawPrint size={30} aria-hidden="true" />}
          </span>
        ))}
        <span className="kp-paneel__aantal">
          <Users size={16} aria-hidden="true" />
          {t("pensionPlanning.paneel.dieren", { args: [groep.length] })}
        </span>
      </div>

      <PaneelBlok titel={t("pensionPlanning.paneel.bewoners")}>
        <ul className="kp-bewoners">
          {groep.map((b) => (
            <li key={b.dier.id}>
              <Foto url={fotos[String(b.dier.id)]} groot />
              <span>
                <strong>{b.dier.naam}</strong>
                <span>{[b.dier.diersoort, b.dier.ras].filter(Boolean).join(" • ")}</span>
                <span>
                  {[b.dier.gewichtKg != null ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(b.dier.gewichtKg)} kg` : null, geslacht(b.dier)]
                    .filter(Boolean)
                    .join(" • ")}
                </span>
                {b.dier.medicatie && (
                  <span className="kp-medicatie">
                    <Pill size={12} aria-hidden="true" />
                    {t("pensionPlanning.chip.medicatie")}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.eigenaar")}>
        <button type="button" className="kp-eigenaar" disabled={!onOpenKlant} onClick={() => onOpenKlant?.(klant.id)}>
          <span className="kp-eigenaar__icoon" aria-hidden="true">
            <UserRound size={18} />
          </span>
          <span>
            <strong>{klant.naam}</strong>
            {(klant.adres || klant.woonplaats) && <span>{[klant.adres, klant.woonplaats].filter(Boolean).join(", ")}</span>}
            {(klant.telefoon || klant.mobiel) && <span>{[klant.telefoon, klant.mobiel].filter(Boolean).join(", ")}</span>}
          </span>
          {onOpenKlant && <ChevronRight size={16} aria-hidden="true" />}
        </button>
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.periode")}>
        <div className="kp-regel">
          <CalendarDays size={15} aria-hidden="true" />
          <span>
            {datum(eerste.van, locale, { day: "2-digit", month: "short", year: "numeric" })} –{" "}
            {datum(eerste.tot, locale, { day: "2-digit", month: "short", year: "numeric" })}
          </span>
          <span className="kp-label-grijs">{t("pensionPlanning.nachten", { args: [verschil(eerste.van, eerste.tot)] })}</span>
        </div>
        {eerste.ingechecktOp && (
          <div className="kp-regel">
            <span className="kp-stip" data-toon="groen" aria-hidden="true" />
            <span>{t("pensionPlanning.paneel.ingecheckt", { args: [moment(eerste.ingechecktOp)] })}</span>
            {!eerste.uitgechecktOp && (
              <span className="kp-label-grijs">
                {t("pensionPlanning.paneel.dagenAanwezig", { args: [Math.max(0, verschil(eerste.ingechecktOp.slice(0, 10), vandaag))] })}
              </span>
            )}
          </div>
        )}
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.geboekt")} uitleg={t("pensionPlanning.paneel.geboektUitleg")}>
        <div className="kp-regel">
          <Sparkles size={15} aria-hidden="true" />
          <span>{eerste.prijsCategorie ?? "—"}</span>
        </div>
        <label className="kp-geplaatst">
          <span>{t("pensionPlanning.paneel.geplaatstIn")}</span>
          <select value={kennel?.id ?? ""} disabled={bezig || eerste.status === "UITGECHECKT"} onChange={(e) => e.target.value && onPlaats(Number(e.target.value))}>
            {!kennel && <option value="">{t("pensionPlanning.paneel.kiesHok")}</option>}
            {passend.map((k) => (
              <option key={k.id} value={k.id}>
                {kennelNaam(k)} · {bezetting(balken, k.id, focus)}/{k.capaciteit}
              </option>
            ))}
          </select>
        </label>
        {!kennel && eerste.status !== "UITGECHECKT" && (
          <AiTip reserveringId={eerste.reserveringId} dierId={eerste.dier.id} t={t} bezig={bezig} onPlaats={onPlaats} />
        )}
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.profiel")}>
        {eerste.opmerking ? <p className="kp-tekst">{eerste.opmerking}</p> : <p className="kp-leeg">{t("pensionPlanning.paneel.geenNotities")}</p>}
        <div className="kp-chips">
          {groep.some((b) => b.eersteBezoek) && <span className="kp-chip" data-toon="accent">{t("pensionPlanning.chip.eersteBezoek")}</span>}
          {eigenschappen.map((e) => (
            <span key={e} className="kp-chip" data-toon="grijs">{e}</span>
          ))}
          {tags.map((tag) => (
            <span key={tag.id} className="kp-chip kp-chip--eigen" style={{ "--kp-tagkleur": tag.color } as CSSProperties}>
              {tag.label}
            </span>
          ))}
        </div>
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.arrangementen")}>
        {arrangementen.length === 0 ? (
          <p className="kp-leeg">{t("pensionPlanning.paneel.geenArrangementen")}</p>
        ) : (
          <ul className="kp-arrangementen">
            {arrangementen.map((a) => (
              <li key={a}>
                <Sparkles size={15} aria-hidden="true" />
                {a}
              </li>
            ))}
          </ul>
        )}
      </PaneelBlok>

      {fout && (
        <p className="kp-fout" role="alert">
          {t("pensionPlanning.paneel.mislukt")}
        </p>
      )}

      <div className="kp-paneel__knoppen">
        <button type="button" className="kp-knop" disabled={!onOpenKlant} onClick={() => onOpenKlant?.(klant.id)}>
          {t("pensionPlanning.paneel.details")}
        </button>
        <button type="button" className="kp-knop" disabled={bezig || eerste.status === "UITGECHECKT"} onClick={() => onVenster("verplaatsen")}>
          <ArrowRightLeft size={15} aria-hidden="true" />
          {t("pensionPlanning.paneel.verplaatsen")}
        </button>
        <button type="button" className="kp-knop kp-knop--hoofd" disabled={bezig || eerste.status === "UITGECHECKT"} onClick={() => onVenster("match")}>
          <Users size={15} aria-hidden="true" />
          {t("pensionPlanning.paneel.groepsmatch")}
        </button>
      </div>
      {kanWeg && (
        <button type="button" className="kp-link kp-paneel__weg" disabled={bezig} onClick={onUitHok}>
          <Trash2 size={14} aria-hidden="true" />
          {t("pensionPlanning.paneel.uitHok")}
        </button>
      )}
    </aside>
  );
}

function PaneelBlok({ titel, uitleg, children }: { titel: string; uitleg?: string; children: ReactNode }) {
  return (
    <section className="kp-paneel__blok">
      <h3>
        {titel}
        {uitleg && (
          <span className="kp-uitleg" title={uitleg}>
            <Info size={15} aria-hidden="true" />
            <span className="kp-verborgen">{uitleg}</span>
          </span>
        )}
      </h3>
      {children}
    </section>
  );
}

function HokPaneel({
  kennel,
  typen,
  berekening,
  balken,
  blokkades,
  bezet,
  focus,
  t,
  locale,
  bezig,
  fout,
  onSluit,
  onKies,
  onZet,
  onBlokkeer,
  onOpheffen,
  onGroepsmatch,
  onBeheer,
}: {
  kennel: Kennel;
  typen: AccommodatieType[];
  berekening: Prijsberekening;
  balken: Balk[];
  blokkades: Blokkade[];
  bezet: number;
  focus: string;
  t: T;
  locale: string;
  bezig: boolean;
  fout: boolean;
  onSluit: () => void;
  onKies: (balk: Balk) => void;
  onZet: (instelling: { capaciteit?: number; accommodatieId?: number }) => void;
  onBlokkeer: (verzoek: { van: string; tot: string; soort: BlokkadeSoort; omschrijving: string | null }) => void;
  onOpheffen: (id: number) => void;
  onGroepsmatch: () => void;
  onBeheer: () => void;
}) {
  const [capaciteit, setCapaciteit] = useState(String(kennel.capaciteit));
  const [accommodatieId, setAccommodatieId] = useState(kennel.accommodatieId != null ? String(kennel.accommodatieId) : "");
  const [blok, setBlok] = useState({ van: focus, tot: focus, soort: "ONDERHOUD" as BlokkadeSoort, omschrijving: "" });
  const type = typen.find((a) => a.id === kennel.accommodatieId);
  const dichtNu = blokkades.some((b) => b.van <= focus && b.tot >= focus);
  const [pil, toon]: [string, Toon] = dichtNu
    ? [t("pensionPlanning.paneel.geblokkeerd"), "rood"]
    : bezet > 0
      ? [t("pensionPlanning.paneel.inGebruik"), "groen"]
      : [t("pensionPlanning.paneel.vrij"), "accent"];
  const naam = kennelNaam(kennel);
  const getal = Number(capaciteit);
  const capaciteitGeldig = Number.isInteger(getal) && getal >= 1 && getal <= 50;
  const prijs = typePrijs(type, berekening, t, locale);

  return (
    <aside className="kp-kaart kp-paneel" aria-label={naam}>
      <PaneelKop icoon={<AccommodatieIcoon type={type} size={22} />} titel={naam}
        sub={[kennel.groep, type?.naam].filter(Boolean).join(" – ")} pil={pil} toon={toon} t={t} onSluit={onSluit} />

      <PaneelBlok titel={t("pensionPlanning.paneel.nuInHok")}>
        {balken.length === 0 ? (
          <p className="kp-leeg">{t("pensionPlanning.paneel.leegHok")}</p>
        ) : (
          <ul className="kp-hokbewoners">
            {balken.map((balk) => (
              <li key={balk.sleutel}>
                <button type="button" className="kp-link" onClick={() => onKies(balk)}>
                  <strong>{namen(balk.boekingen)}</strong> • {balk.boekingen[0].klant.naam}
                </button>
                <span className="kp-label-grijs">
                  {datum(balk.van, locale, { day: "numeric", month: "short" })} – {datum(balk.tot, locale, { day: "numeric", month: "short" })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.instellingen")}>
        <form
          className="kp-formulier"
          onSubmit={(e) => {
            e.preventDefault();
            if (capaciteitGeldig) onZet({ capaciteit: getal, ...(accommodatieId ? { accommodatieId: Number(accommodatieId) } : {}) });
          }}
        >
          <label>
            <span>
              {t("pensionPlanning.paneel.capaciteit")}
              {!kennel.capaciteitIngesteld && <em> ({t("pensionPlanning.paneel.capaciteitStandaard")})</em>}
            </span>
            <input type="number" min={1} max={50} value={capaciteit} onChange={(e) => setCapaciteit(e.target.value)} />
          </label>
          <label>
            <span>
              {t("pensionPlanning.paneel.accommodatie")}
              {type && !kennel.accommodatieIngesteld && <em> ({t("pensionPlanning.paneel.accommodatieAfgeleid")})</em>}
            </span>
            <select value={accommodatieId} onChange={(e) => setAccommodatieId(e.target.value)}>
              {!kennel.accommodatieId && <option value="">{t("pensionPlanning.filters.zonderType")}</option>}
              {typen.map((a) => (
                <option key={a.id} value={a.id}>{a.naam}</option>
              ))}
            </select>
          </label>
          {prijs && <p className="kp-leeg">{prijs}</p>}
          <div className="kp-formulier__knoppen">
            <button type="submit" className="kp-knop" disabled={bezig || !capaciteitGeldig}>
              {t("pensionPlanning.paneel.opslaan")}
            </button>
            <button type="button" className="kp-link" onClick={onBeheer}>
              <Settings2 size={14} aria-hidden="true" />
              {t("pensionPlanning.filters.beheren")}
            </button>
          </div>
        </form>
      </PaneelBlok>

      <PaneelBlok titel={t("pensionPlanning.paneel.blokkades")}>
        {blokkades.length === 0 ? (
          <p className="kp-leeg">{t("pensionPlanning.paneel.geenBlokkades")}</p>
        ) : (
          <ul className="kp-hokbewoners">
            {blokkades.map((b) => (
              <li key={b.id}>
                <span>
                  <Wrench size={14} aria-hidden="true" data-toon="rood" /> <strong>{b.omschrijving ?? t(`pensionPlanning.blokkade.${b.soort}`)}</strong>
                </span>
                <span className="kp-label-grijs">
                  {datum(b.van, locale, { day: "numeric", month: "short" })} – {datum(b.tot, locale, { day: "numeric", month: "short" })}
                </span>
                <button type="button" className="kp-link" disabled={bezig} onClick={() => onOpheffen(b.id)}>
                  {t("pensionPlanning.paneel.opheffen")}
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          className="kp-formulier"
          onSubmit={(e) => {
            e.preventDefault();
            onBlokkeer({ ...blok, omschrijving: blok.omschrijving.trim() || null });
          }}
        >
          <div className="kp-formulier__rij">
            <label>
              <span>{t("pensionPlanning.filters.van")}</span>
              <DateField required value={blok.van} onChange={(e) => setBlok({ ...blok, van: e.target.value })} />
            </label>
            <label>
              <span>{t("pensionPlanning.filters.tot")}</span>
              <DateField required min={blok.van} value={blok.tot} onChange={(e) => setBlok({ ...blok, tot: e.target.value })} />
            </label>
          </div>
          <label>
            <span>{t("pensionPlanning.paneel.soort")}</span>
            <select value={blok.soort} onChange={(e) => setBlok({ ...blok, soort: e.target.value as BlokkadeSoort })}>
              <option value="ONDERHOUD">{t("pensionPlanning.blokkade.ONDERHOUD")}</option>
              <option value="GEBLOKKEERD">{t("pensionPlanning.blokkade.GEBLOKKEERD")}</option>
            </select>
          </label>
          <label>
            <span>{t("pensionPlanning.paneel.omschrijving")}</span>
            <input type="text" maxLength={200} value={blok.omschrijving} onChange={(e) => setBlok({ ...blok, omschrijving: e.target.value })} />
          </label>
          <button type="submit" className="kp-knop kp-knop--gevaar"
            disabled={bezig || !geldigeDatum(blok.van) || !geldigeDatum(blok.tot) || blok.tot < blok.van}>
            <Wrench size={15} aria-hidden="true" />
            {t("pensionPlanning.paneel.blokkeren")}
          </button>
        </form>
      </PaneelBlok>

      {fout && (
        <p className="kp-fout" role="alert">
          {t("pensionPlanning.paneel.mislukt")}
        </p>
      )}

      <div className="kp-paneel__knoppen">
        <button type="button" className="kp-knop kp-knop--hoofd" disabled={bezig} onClick={onGroepsmatch}>
          <Users size={15} aria-hidden="true" />
          {t("pensionPlanning.paneel.groepsmatch")}
        </button>
      </div>
    </aside>
  );
}

function MatchVenster({
  venster,
  boekingen,
  nogTePlaatsen,
  kennels,
  typen,
  balken,
  blokkades,
  periode,
  focus,
  t,
  bezig,
  onSluit,
  onPlaats,
}: {
  venster: Venster;
  boekingen: Boeking[];
  nogTePlaatsen: Boeking[];
  kennels: Kennel[];
  typen: Map<number, AccommodatieType>;
  balken: Balk[];
  blokkades: Blokkade[];
  periode: { van: string; tot: string };
  focus: string;
  t: T;
  bezig: boolean;
  onSluit: () => void;
  onPlaats: (reserveringId: number, dierIds: number[], kennelId: number) => void;
}) {
  const reden = (v: Voorstel, n: number) => [
    ...v.redenen.map((r) => t(`pensionPlanning.match.reden.${r}`)),
    t("pensionPlanning.match.reden.ruimte", { args: [Math.max(0, v.kennel.capaciteit - v.bezet - n), v.kennel.capaciteit] }),
  ];

  if (venster.soort === "hokMatch") {
    const kennel = kennels.find((k) => k.id === venster.kennelId);
    if (!kennel) return null;
    const perReservering = new Map<number, Boeking[]>();
    for (const b of nogTePlaatsen) perReservering.set(b.reserveringId, [...(perReservering.get(b.reserveringId) ?? []), b]);
    const kandidaten = [...perReservering.values()]
      .map((groep) => ({ groep, voorstel: groepsmatch(groep, [kennel], typen, balken, blokkades, periode)[0] }))
      .filter((k) => k.voorstel?.past)
      .sort((a, b) => b.voorstel.score - a.voorstel.score);
    return (
      <Modal open title={t("pensionPlanning.match.hokTitel", { args: [kennelNaam(kennel)] })} onClose={onSluit}>
        <div className="pension-planning kp-venster">
          {kandidaten.length === 0 ? (
            <p className="kp-leeg">{t("pensionPlanning.match.geenDieren")}</p>
          ) : (
            <ul className="kp-voorstellen">
              {kandidaten.map(({ groep, voorstel }) => (
                <li key={groep[0].reserveringId} data-past="">
                  <span>
                    <strong>{namen(groep)}</strong> • {groep[0].klant.naam}
                    <span className="kp-chips">
                      {reden(voorstel, groep.length).map((r) => (
                        <span key={r} className="kp-chip" data-toon="grijs">{r}</span>
                      ))}
                    </span>
                  </span>
                  <button type="button" className="kp-knop kp-knop--hoofd" disabled={bezig}
                    aria-label={t("pensionPlanning.match.plaatsDier", { args: [namen(groep)] })}
                    onClick={() => onPlaats(groep[0].reserveringId, groep.map((b) => b.dier.id), kennel.id)}>
                    {t("pensionPlanning.match.plaatsen")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
    );
  }

  const groep = boekingen.filter((b) => b.reserveringId === venster.reserveringId && venster.dierIds.includes(b.dier.id));
  if (groep.length === 0) return null;
  const huidig = groep[0].kennelId;
  const voorstellen = groepsmatch(groep, kennels, typen, balken, blokkades, periode).filter((v) => v.kennel.id !== huidig);
  const lijst =
    venster.soort === "match"
      ? voorstellen.filter((v) => v.past).sort((a, b) => b.score - a.score).slice(0, VOORSTELLEN)
      : voorstellen;
  const titel =
    venster.soort === "match"
      ? t("pensionPlanning.match.titel", { args: [namen(groep)] })
      : t("pensionPlanning.verplaatsen.titel", { args: [namen(groep)] });

  return (
    <Modal open title={titel} onClose={onSluit}>
      <div className="pension-planning kp-venster">
        {lijst.length === 0 ? (
          <p className="kp-leeg">{t("pensionPlanning.match.geen")}</p>
        ) : (
          <ul className="kp-voorstellen">
            {lijst.map((v) => {
              const type = v.kennel.accommodatieId != null ? typen.get(v.kennel.accommodatieId) : undefined;
              return (
                <li key={v.kennel.id} data-past={v.past ? "" : undefined}>
                  <AccommodatieIcoon type={type} size={18} />
                  <span>
                    <strong>{kennelNaam(v.kennel)}</strong> • {v.kennel.groep}
                    <span className="kp-label-grijs"> · {bezetting(balken, v.kennel.id, focus)}/{v.kennel.capaciteit}</span>
                    <span className="kp-chips">
                      {v.past ? (
                        reden(v, groep.length).map((r) => (
                          <span key={r} className="kp-chip" data-toon={r === t("pensionPlanning.match.reden.accommodatie") ? "amber" : "grijs"}>{r}</span>
                        ))
                      ) : (
                        <span className="kp-chip" data-toon="rood">{t("pensionPlanning.verplaatsen.vol")}</span>
                      )}
                    </span>
                  </span>
                  <button type="button" className={v.past ? "kp-knop kp-knop--hoofd" : "kp-knop"} disabled={bezig}
                    aria-label={t("pensionPlanning.match.plaatsenIn", { args: [kennelNaam(v.kennel)] })}
                    onClick={() => onPlaats(groep[0].reserveringId, groep.map((b) => b.dier.id), v.kennel.id)}>
                    {t("pensionPlanning.match.plaatsen")}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
